import { SchemaFormFields, useHandledDialogFormControls } from '@mews-commander/form';
import { PortfolioBookablePeriod, PortfolioServiceType, useRepositoryMutation } from '@mews-commander/data-access';
import { lt, showSuccessAlert, useLocalizeCurrency, createZodFormResolver } from '@mews/framework';
import type { EditServicesProductProps, ServicesProductFormValues } from '../types';
import { servicesProductSchema } from '../schemas';
import { useCurrencyFormat } from '../../../../../../core/globalization/hooks/useCurrencyFormat';
import { usePaymentChargingOptions } from '../../../ConfigureProductForm/hooks';
import { useProductOptions } from '../../../../hooks/useProductOptions';
import { editServicesProductFormDefinition } from './formDefinition';
import { mapAdjustmentDefaultValues, mapFormPriceAdjustmentToApi } from './mappers';

const EditServicesProductFormContent = ({
    serviceType,
    bookablePeriod,
    basePriceValue,
}: Pick<EditServicesProductProps, 'bookablePeriod' | 'serviceType' | 'basePriceValue'>) => {
    const currencyFormat = useCurrencyFormat(basePriceValue.currency);
    const currencySymbol = currencyFormat.symbol;
    const localizeCurrency = useLocalizeCurrency();
    const localizedCurrency = localizeCurrency(basePriceValue.netValue, basePriceValue.currency);

    // TODO: This is a temporary solution to get the charging options.
    // Remove type assertion when enterpriseProductEdit refactor is done.
    // https://mews.atlassian.net/browse/MMP-11794
    const chargingOptions = usePaymentChargingOptions({
        bookablePeriod: bookablePeriod as PortfolioBookablePeriod | undefined,
        serviceType: serviceType as PortfolioServiceType | undefined,
    });

    const filteredProductOptions = useProductOptions({ bookablePeriod });

    return (
        <SchemaFormFields
            schema={servicesProductSchema}
            formDefinition={editServicesProductFormDefinition({
                basePriceValue,
                currencySymbol,
                localizedCurrency,
                chargingOptions,
                productOptions: filteredProductOptions,
            })}
        />
    );
};

export const EditServicesProductForm = ({
    centralProductId,
    enterpriseProductId,
    propertyId,
    options,
    promotions,
    bookablePeriod,
    adjustment,
    basePriceValue,
    consumptionMoment,
    charging,
    serviceType,
    productId,
    serviceId,
    ...formProps
}: EditServicesProductProps) => {
    const { mutateAsync } = useRepositoryMutation('portfolioEnterpriseProducts', 'updateServiceProduct', {
        onSuccess: () => showSuccessAlert({ title: lt('ChangesSaved') }),
        invalidateQueries: {
            portfolioEnterpriseProducts: ['getDetail'],
        },
    });

    const { renderFormContent } = useHandledDialogFormControls<ServicesProductFormValues>({
        ...formProps,
        values: {
            options: options || [],
            promotions: promotions || [],
            repriceExistingReservations: false,
            priceAdjustment: mapAdjustmentDefaultValues({ basePriceValue, adjustment }),
            charging,
            consumptionMoment: consumptionMoment ?? null,
        },
        onSubmit: async (formValues) => {
            await mutateAsync({
                ...formValues,
                centralProductId,
                enterpriseProductId,
                propertyId,
                productId,
                serviceId,
                priceAdjustment: mapFormPriceAdjustmentToApi({
                    priceAdjustment: formValues.priceAdjustment,
                    basePriceValue,
                    originalAdjustment: adjustment,
                }),
            });
        },
        resolver: createZodFormResolver(servicesProductSchema),
    });

    return renderFormContent(
        <EditServicesProductFormContent
            basePriceValue={basePriceValue}
            bookablePeriod={bookablePeriod}
            serviceType={serviceType}
        />
    );
};
